import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';
import { AiImagePicker, type AiImage } from '@/components/ai-image-picker';

export function GenerateForm() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [kind, setKind] = useState('newsletter');
  const [brief, setBrief] = useState('');
  const [styles, setStyles] = useState<Array<{ id: string; name: string; description: string | null; exampleCount: number }>>([]);
  const [styleId, setStyleId] = useState('');
  useEffect(() => {
    api<Array<{ id: string; name: string; description: string | null; exampleCount: number }>>('/styles').then(setStyles).catch(() => setStyles([]));
  }, []);
  const [images, setImages] = useState<AiImage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api<{ id: string; warnings: string[]; model: string }>('/templates/generate', {
        method: 'POST',
        json: { name, subject, preheader: preheader || undefined, kind, brief, styleId: styleId || undefined, images: images.map(({ filename, contentType, base64, note }) => ({ filename, contentType, base64, note: note || undefined })) },
      });
      navigate(`/templates/${created.id}${created.warnings.length ? `?warnings=${encodeURIComponent(created.warnings.join(' · '))}` : ''}`);
    } catch (err) {
      setError(errMsg(err, 'Generation failed'));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-4 rounded-lg border bg-white p-4 text-sm lg:grid-cols-[320px_1fr]">
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Name</span>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-full rounded-md border px-2" />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Subject</span>
          <input required value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 w-full rounded-md border px-2" />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Preheader</span>
          <input value={preheader} onChange={(e) => setPreheader(e.target.value)} className="h-9 w-full rounded-md border px-2" />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Style (guidelines + examples the AI follows)</span>
          <select value={styleId} onChange={(e) => setStyleId(e.target.value)} className="h-9 w-full rounded-md border px-2">
            <option value="">General (all flagged examples)</option>
            {styles.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.description ? ` — ${s.description}` : ''}
                {s.exampleCount ? ` (${s.exampleCount} ex.)` : ' (no examples yet)'}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 w-full rounded-md border px-2">
            <option value="newsletter">Newsletter (marketing domain)</option>
            <option value="flow">Flow email (marketing domain)</option>
            <option value="transactional">Transactional (product domain)</option>
          </select>
        </label>
        <Button type="submit" disabled={busy || brief.trim().length < 10 || !name || !subject}>
          {busy ? 'Generating… (20–60s)' : 'Generate template'}
        </Button>
        {error && (
          <p role="alert" className="rounded-md bg-red-50 p-2 text-xs text-red-800">
            {error}
          </p>
        )}
      </div>
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Brief — what is this email about, who is it for, tone, sections, CTA</span>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={10}
            className="w-full rounded-md border p-2"
            placeholder={'Announce the new Silhouette to Model feature to all users. Friendly, short. Sections: what it is, 3 bullet benefits, a screenshot, CTA "Try Silhouette to Model" → https://facetflow.ai. Sign off as Sarosha.'}
          />
        </label>
        <AiImagePicker images={images} onChange={setImages} disabled={busy} />
        <p className="text-xs text-muted-foreground">The Facet Flow logo and the style's important links (see Style library) are always available to the model — no upload needed. Images are uploaded to Firebase Storage first; the model places them by your notes. A {'{{first_name}}'} greeting and (for marketing) an unsubscribe footer are required and checked.</p>
      </div>
    </form>
  );
}
